<template>
  <div>
    <v-app-bar flat dense color="primary" max-height="48px">
      <v-toolbar-title>
        <template v-for="command in headerCommands">
          <v-btn
            v-if="command.icon === null"
            :key="command.value"
            color="white"
            text
            rounded
            :disabled="command.isDisabled"
            @click="command.onClick"
            >{{ command.text }}</v-btn
          >
          <v-btn
            v-else
            :key="command.text"
            color="white"
            icon
            large
            @click="command.onClick"
            :disabled="command.isDisabled"
          >
            <v-icon>{{ command.icon }}</v-icon>
          </v-btn>
        </template>
        <span v-if="fileName" class="white--text"> {{ fileName }} </span>
      </v-toolbar-title>
      <v-spacer></v-spacer>
      <v-btn
        color="white"
        icon
        @click="toggleFullScreen"
        :disabled="fullScreenDisabled"
      >
        <v-icon color="white">{{ fullScreenIcon }}</v-icon>
      </v-btn>
      <v-btn
        color="white"
        icon
        @click="resetZoomClick"
        :disabled="!dataAvailable"
      >
        <v-icon color="white">find_replace</v-icon>
      </v-btn>
      <v-btn
        color="white"
        icon
        @click="zoomOutClick"
        :disabled="!dataAvailable || currentZoom <= 6"
      >
        <v-icon color="white">zoom_out</v-icon>
      </v-btn>
      <v-btn
        color="white"
        icon
        @click="zoomInClick"
        :disabled="!dataAvailable || currentZoom >= 40"
      >
        <v-icon color="white">zoom_in</v-icon>
      </v-btn>
      <v-btn color="white" icon @click="toggleInfo">
        <v-icon color="white">info</v-icon>
      </v-btn>
      <v-btn color="white" icon @click="openSettings">
        <v-icon color="white">settings</v-icon>
      </v-btn>
    </v-app-bar>
  </div>
</template>

<script>
import {
  LOAD_FILE_DIALOG,
  SAVE_FILE,
  CHANGE_FILE_NAME,
  CREATE_NEW_FILE
} from "../../store/modules/uptData/constants";
import { NEW_OK_CANCEL_DIALOG } from "../../store/modules/okCancelDialog/constants.js";
import { TOGGLE_SHOW_EDITOR } from "../../store/modules/settings/constants.js";
import { TOGGLE_SHOW_INFO } from "../../store/modules/showComponents/constants.js";
import { NEW_GENERIC_INPUT_DIALOG } from "../../store/modules/genericInputDialog/constants.js";
import {
  ZOOM_IN,
  ZOOM_OUT,
  RESET_ZOOM
} from "../../store/modules/tableView/constants.js";
const _ = require("lodash");
import screenfull from "screenfull";

export default {
  name: "EditHeader",

  data: function() {
    return {
      downloadFileCommand: new headerCommand(
        "Load file",
        "cloud_download",
        this.checkForMutation,
        this.dataAvailable
      ),
      uploadFileCommand: new headerCommand(
        "Upload file",
        "cloud_upload",
        this.saveFile,
        false
      ),
      createNewFileCommand: new headerCommand(
        "Create new pattern",
        "add",
        this.createNewFile,
        false
      ),
      fullScreenIcon: "fullscreen",
      fullScreenDisabled: false
    };
  },

  created: function() {
    this.fullScreenDisabled = window.matchMedia(
      "(display-mode: standalone)"
    ).matches;
  },

  computed: {
    headerCommands() {
      return [
        this.downloadFileCommand,
        this.uploadFileCommand,
        this.createNewFileCommand
      ];
    },
    dataAvailable() {
      return this.$store.state.uptData.dataAvailable;
    },
    currentZoom() {
      return this.$store.state.tableView.zoom;
    },
    hasChanges() {
      return this.$store.state.uptData.modified;
    },
    fileName() {
      return this.$store.state.uptData.fileName;
    },
    redirectAfterSave() {
      return this.$store.state.settings.redirectAfterSave;
    },
    canSave() {
      return this.$store.state.uptData.canSave;
    }
  },

  watch: {
    hasChanges: {
      immediate: true,
      handler: function(newValue) {
        if (!this.redirectAfterSave) {
          this.uploadFileCommand.isDisabled = !this.canSave || !newValue;
        }
      }
    },
    redirectAfterSave: {
      immediate: true,
      handler: function(newValue) {
        if (newValue) {
          this.downloadFileCommand.isDisabled = true;
          this.createNewFileCommand.isDisabled = true;
        }
      }
    }
  },

  methods: {
    zoomInClick() {
      this.$store.dispatch(ZOOM_IN);
    },
    zoomOutClick() {
      this.$store.dispatch(ZOOM_OUT);
    },
    resetZoomClick() {
      this.$store.dispatch(RESET_ZOOM);
    },
    toggleFullScreen() {
      if (screenfull.enabled) {
        this.fullScreenIcon = screenfull.isFullscreen
          ? "fullscreen"
          : "fullscreen_exit";
        screenfull.toggle();
      }
    },
    openFileLoaderDialog() {
      this.$store.dispatch(LOAD_FILE_DIALOG);
    },
    openSettings() {
      this.$store.dispatch(TOGGLE_SHOW_EDITOR);
    },
    async saveFile() {
      return await this.$store.dispatch(SAVE_FILE);
    },
    async fileHasChangesDialog() {
      const fileHasChanges = await this.$store.dispatch(NEW_OK_CANCEL_DIALOG, {
        title: "Warning",
        text: `Save changes of ${this.fileName}?`,
        okText: "Save changes",
        cancelText: "No",
        icon: "warning"
      });
      if (fileHasChanges) {
        return !(await this.saveFile());
      }
      return fileHasChanges;
    },
    async checkForMutation() {
      if (this.hasChanges) {
        await this.fileHasChangesDialog();
        this.openFileLoaderDialog();
      } else {
        this.openFileLoaderDialog();
      }
    },
    async createNewFile() {
      if (this.hasChanges) {
        const notOverride = await this.fileHasChangesDialog();
        if (notOverride) {
          return;
        }
      }
      const dialogResult = await this.$store.dispatch(
        NEW_GENERIC_INPUT_DIALOG,
        {
          title: "Create new pattern",
          okText: "Create pattern",
          icon: "add",
          autofocus: "numberOfPicks",
          content: [
            {
              id: "numberOfPicks",
              type: "numberInput",
              initialValue: "2",
              inputLabel: "# of picks",
              min: 2,
              max: 100000
            },
            {
              id: "numberOfJacquardHooks",
              type: "numberInput",
              initialValue: 0,
              inputLabel: "# of jacquard hooks",
              min: 0,
              max: 3072,
              createHint: v => {
                if (v % 8 === 0) {
                  return "";
                } else {
                  return `${v} is not dividable by 8. Will be set to ${Math.ceil(
                    v / 8
                  ) * 8}`;
                }
              }
            },
            {
              id: "numberOfShafts",
              type: "numberInput",
              initialValue: 0,
              inputLabel: "# of shafts",
              min: 0,
              max: 32
            }
          ]
        }
      );
      if (!dialogResult.cancelled && _.toNumber(dialogResult.numberOfPicks)) {
        this.$store.dispatch(CREATE_NEW_FILE, {
          nrOfPicks: _.toNumber(dialogResult.numberOfPicks),
          nrOfJacquardHooks: _.toNumber(dialogResult.numberOfJacquardHooks),
          nrOfShafts: _.toNumber(dialogResult.numberOfShafts),
          filename: "NewFile.UPT"
        });
      }
    },
    async changeFileName() {
      const dialogResult = await this.$store.dispatch(
        NEW_GENERIC_INPUT_DIALOG,
        {
          title: "Change file name",
          okText: "Apply changes",
          icon: "edit",
          autofocus: "newFileName",
          content: [
            {
              id: "newFileName",
              type: "textInput",
              initialValue: this.fileName,
              createValidationErrorMessage: s => {
                if (s.includes(".")) {
                  return "File can not contain '.'";
                }
                return "";
              },
              append: ".UPT"
            }
          ]
        }
      );
      if (!dialogResult.cancelled && dialogResult.newFileName) {
        this.$store.dispatch(CHANGE_FILE_NAME, dialogResult.newFileName);
      }
    },
    toggleInfo() {
      this.$store.dispatch(TOGGLE_SHOW_INFO);
    }
  }
};

function headerCommand(text, icon = null, onClick, isDisabled) {
  this.text = text;
  this.icon = icon;
  this.onClick = onClick;
  this.isDisabled = isDisabled;
}
</script>
<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped></style>
